import React, { useState } from "react";
import { Row, Col, Card, Avatar, Typography, Empty } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { getUserList, getUserInfo } from "redux/selectors";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

const { Text } = Typography;

const Wrapper = styled.div`
  padding: 16px;
`;

const UserCard = styled(Card)`
  text-align: center;
  border-radius: 14px;
  background: #f2fffb;
  border: 1px solid #d1fae5;

  .ant-card-body {
    padding: 24px 16px;
  }
`;

const Name = styled(Text)`
  display: block;
  font-weight: 600;
  margin-top: 12px;
`;

const Role = styled(Text)`
  color: #6b7280;
  font-size: 13px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
  gap: 8px;
`;

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  background: ${(props) => (props.disabled ? "#f3f4f6" : "#ffffff")};
  color: ${(props) => (props.disabled ? "#d1d5db" : "#374151")};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  padding: 0;
  line-height: 1;

  &:hover:not(:disabled) {
    background: #1677ff;
    border-color: #1677ff;
    color: #ffffff;
  }
`;

const DotsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => (props.active ? "#1677ff" : "#d1d5db")};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${(props) => (props.active ? "#1677ff" : "#9ca3af")};
  }
`;

const SUPER_ADMIN_ROLE_ID = "2";

const RecentJoiners = ({ userList = [], userInfo }) => {
  const [page, setPage] = useState(0);

  const itemsPerPage =
    window.innerWidth < 576 ? 1 : window.innerWidth < 992 ? 2 : 3;

  const currentUserOffice = Array.isArray(userInfo?.managingoffice)
    ? userInfo.managingoffice[0]
    : userInfo?.managingoffice;


  const getRecentJoiners = (list = []) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return list.filter((user) => {
      if (!user.hireddate) return false;
      if (user.statusid === "0" || user.statusname === "inactive") return false;
      const hiredDate = new Date(user.hireddate);
      return (
        hiredDate.getMonth() === currentMonth &&
        hiredDate.getFullYear() === currentYear
      );
    });
  };

  const filterByCountry = (list = []) => {

    if (!currentUserOffice) return [];

    return list.filter((user) => {
      const userOffice = Array.isArray(user.managingoffice)
        ? user.managingoffice[0]
        : user.managingoffice;


      return userOffice === currentUserOffice;
    });
  };

  const recentJoiners = getRecentJoiners(filterByCountry(userList)).map(
    (u) => ({
      name: u?.fullname,
      role: u?.rolename?.[0] || "Employee",
      image: u.profileurl || null,
    })
  );

  const totalPages = Math.ceil(recentJoiners.length / itemsPerPage);

  const visibleUsers = recentJoiners.slice(
    page * itemsPerPage,
    page * itemsPerPage + itemsPerPage
  );

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  return (
    <Wrapper>
      <h6>Recent Joiners</h6>

      {recentJoiners.length === 0 ? (
        <Empty description="No data" style={{ marginTop: 40 }} />
      ) : (
        <Row gutter={[16, 16]} justify="center">
          {visibleUsers.map((user, index) => (
            <Col xs={24} sm={12} md={8} key={index}>
              <UserCard>
                <Avatar src={user.image} size={80}>
                  {!user.image && user.name?.[0]?.toUpperCase()}
                </Avatar>
                <Name>{user.name}</Name>
                <Role>{user.role}</Role>
              </UserCard>
            </Col>
          ))}
        </Row>
      )}

      {totalPages > 1 && (
        <PaginationWrapper>
          <ArrowButton
            onClick={handlePrev}
            disabled={page === 0}
            aria-label="Previous page"
          >
            <LeftOutlined style={{ fontSize: 11 }} />
          </ArrowButton>

          <DotsWrapper>
            {Array.from({ length: totalPages }).map((_, i) => (
              <Dot key={i} active={i === page} onClick={() => setPage(i)} />
            ))}
          </DotsWrapper>

          <ArrowButton
            onClick={handleNext}
            disabled={page === totalPages - 1}
            aria-label="Next page"
          >
            <RightOutlined style={{ fontSize: 11 }} />
          </ArrowButton>
        </PaginationWrapper>
      )}
    </Wrapper>
  );
};

const mapStateToProps = (state) => ({
  userList: getUserList(state),
  userInfo: getUserInfo(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(RecentJoiners);